import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Maximize2,
} from 'lucide-react';
import { DemoDocumentFinding, DemoImageFinding, demoApi } from '../../api/demo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

const DOC_TYPE_LABELS: Record<string, string> = {
  radiografia: 'Radiografia',
  modulo_consulto: 'Modulo di consulto',
  foto_clinica: 'Foto clinica',
  referto_imaging: 'Referto imaging',
  referto_esame: 'Referto esame',
  citologia: 'Citologia',
  scheda_visita: 'Scheda visita',
  non_clinico: 'Non clinico',
  altro: 'Altro',
};

const STATUS_LABELS: Record<string, string> = {
  ok: 'Analizzata',
  failed: 'Non analizzata',
  skipped: 'Saltata',
};

const SOURCE_LABELS: Record<string, string> = {
  text: 'Testo',
  scan: 'Scansione',
};

/** Caratteri di estratto mostrati prima di "Mostra tutto". */
const EXCERPT_PREVIEW_LENGTH = 400;

/** L'object URL dell'originale resta vivo finche' la nuova scheda non lo carica. */
const OBJECT_URL_LIFETIME_MS = 60_000;

/**
 * Immagine originale di un allegato: il fetch e' autenticato (bearer), quindi
 * passa dall'API client e diventa un object URL, revocato allo smontaggio.
 */
export function DemoAttachmentImage({
  resultId,
  sha256,
  page,
  alt,
  className,
}: {
  resultId: string;
  sha256: string;
  /** Pagina 1-based per i PDF; assente per i JPEG. */
  page?: number | null;
  alt: string;
  className?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    const pending =
      page != null
        ? demoApi.getCaseAttachment(resultId, sha256, page)
        : demoApi.getCaseAttachment(resultId, sha256);
    pending
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resultId, sha256, page]);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center text-center text-[10px] text-muted-foreground ${className ?? ''}`}
      >
        Immagine non disponibile
      </div>
    );
  }
  if (!url) {
    return (
      <div
        aria-hidden="true"
        className={`animate-pulse bg-muted/40 ${className ?? ''}`}
      />
    );
  }
  return <img src={url} alt={alt} className={className} />;
}

/**
 * Estratto del documento: anteprima troncata con espansione esplicita, cosi'
 * il testo lungo non spinge fuori schermo il resto della card.
 */
function DemoDocumentExcerpt({
  sha256,
  text,
}: {
  sha256: string;
  text: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const truncated = text.length > EXCERPT_PREVIEW_LENGTH;
  const visible =
    expanded || !truncated ? text : text.slice(0, EXCERPT_PREVIEW_LENGTH);

  return (
    <div className="rounded-md border border-border bg-background/60 p-2">
      <p
        data-testid={`demo-document-excerpt-${sha256.slice(0, 8)}`}
        className="whitespace-pre-wrap text-muted-foreground"
      >
        {visible}
        {truncated && !expanded ? '…' : ''}
      </p>
      {truncated && (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="mt-1 h-6 px-2 text-[11px]"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Mostra meno' : 'Mostra tutto'}
        </Button>
      )}
    </div>
  );
}

/**
 * Originale del documento: il blob arriva dallo stesso endpoint autenticato
 * delle immagini, si apre in una nuova scheda e l'object URL viene revocato
 * solo quando la scheda ha avuto modo di caricarlo.
 */
function DemoDocumentOpenButton({
  resultId,
  document,
}: {
  resultId: string;
  document: DemoDocumentFinding;
}) {
  const [isOpening, setIsOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openOriginal = async () => {
    setIsOpening(true);
    setError(null);
    try {
      const blob = await demoApi.getCaseAttachment(resultId, document.sha256);
      const url = URL.createObjectURL(blob);
      try {
        window.open(url, '_blank');
      } catch (openError) {
        // `window.open` puo' lanciare (popup bloccati): senza revoca l'object
        // URL resterebbe vivo fino alla chiusura della scheda.
        URL.revokeObjectURL(url);
        throw openError;
      }
      window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_LIFETIME_MS);
    } catch {
      setError("Impossibile aprire l'originale. Riprova.");
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <div className="space-y-1">
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-6 px-2 text-[11px]"
        disabled={isOpening}
        onClick={openOriginal}
      >
        <ExternalLink className="size-3" />
        Apri originale
      </Button>
      {error && (
        <div role="alert" className="text-danger">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Un reperto documentale: tipo, fonte (testo/scansione), pagine lette, stato
 * mai nascosto (ok / non analizzata / saltata con motivo) ed estratto redatto.
 */
function DemoDocumentCard({
  resultId,
  document,
}: {
  resultId: string;
  document: DemoDocumentFinding;
}) {
  const isAnalyzed = document.status === 'ok';

  return (
    <article className="space-y-1.5 rounded-lg border border-border bg-muted/10 p-3 text-xs">
      <div className="flex flex-wrap items-center gap-1.5">
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-semibold text-foreground">
          {document.title ?? document.filename ?? document.logical_path}
        </span>
        {document.title && document.filename && (
          <span className="truncate text-muted-foreground">
            {document.filename}
          </span>
        )}
        <Badge
          variant={isAnalyzed ? 'secondary' : 'outline'}
          data-testid={`demo-document-status-${document.sha256.slice(0, 8)}`}
        >
          {STATUS_LABELS[document.status] ?? document.status}
        </Badge>
        {document.source && (
          <Badge variant="outline">
            {SOURCE_LABELS[document.source] ?? document.source}
          </Badge>
        )}
        {document.doc_type && (
          <Badge variant="outline">
            {DOC_TYPE_LABELS[document.doc_type] ?? document.doc_type}
          </Badge>
        )}
      </div>
      <div className="text-muted-foreground">
        Pagine lette:{' '}
        <span className="text-foreground">
          {document.pages_read}/{document.pages_total}
        </span>
      </div>
      {document.text_excerpt && (
        <DemoDocumentExcerpt
          sha256={document.sha256}
          text={document.text_excerpt}
        />
      )}
      {!isAnalyzed && (
        <div className="flex items-start gap-1.5 text-danger">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{document.reason ?? 'Motivo non disponibile'}</span>
        </div>
      )}
      {document.limits.length > 0 && (
        <div className="text-muted-foreground">
          Limiti: {document.limits.join('; ')}
        </div>
      )}
      <DemoDocumentOpenButton resultId={resultId} document={document} />
    </article>
  );
}

/**
 * Sezione "Referti e immagini" del dettaglio caso: un reperto per immagine
 * analizzata e per PDF letto, con stato mai nascosto e accesso all'originale.
 */
export function DemoImagesPanel({
  resultId,
  images,
  documents,
  onOpen,
}: {
  resultId: string;
  images: DemoImageFinding[];
  documents: DemoDocumentFinding[];
  onOpen: (image: DemoImageFinding) => void;
}) {
  if (images.length === 0 && documents.length === 0) return null;
  const analyzed =
    images.filter((image) => image.status === 'ok').length +
    documents.filter((document) => document.status === 'ok').length;

  return (
    <Card
      data-testid="demo-images-panel"
      className="border-primary/20 bg-card overflow-hidden shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-primary/10 bg-primary/5 px-4 py-3">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <ImageIcon className="size-4 text-primary" />
          Referti e immagini
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {images.length > 0 && (
            <Badge variant="outline" className="bg-background/60">
              {images.length} immagini
            </Badge>
          )}
          {documents.length > 0 && (
            <Badge variant="outline" className="bg-background/60">
              {documents.length} documenti
            </Badge>
          )}
          <Badge variant="outline" className="bg-background/60">
            {analyzed} analizzate
          </Badge>
        </div>
      </div>
      <CardContent className="space-y-4 p-4">
        {images.length > 0 && (
          <div className="grid gap-4 lg:grid-cols-2">
            {images.map((image) => (
              <article
                key={`${image.sha256}-${image.logical_path}`}
                className="flex gap-3 rounded-lg border border-border bg-muted/10 p-3"
              >
                <button
                  type="button"
                  onClick={() => onOpen(image)}
                  aria-label={`Apri immagine ${image.filename ?? image.logical_path}`}
                  className="h-24 w-24 shrink-0 overflow-hidden rounded-md border border-border bg-muted/30"
                >
                  <DemoAttachmentImage
                    resultId={resultId}
                    sha256={image.sha256}
                    page={image.page}
                    alt={image.filename ?? 'Allegato'}
                    className="h-24 w-24 object-cover"
                  />
                </button>
                <div className="min-w-0 flex-1 space-y-1.5 text-xs">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate font-semibold text-foreground">
                      {image.filename ?? image.logical_path}
                    </span>
                    <Badge
                      variant={image.status === 'ok' ? 'secondary' : 'outline'}
                      data-testid={`demo-image-status-${image.sha256.slice(0, 8)}`}
                    >
                      {STATUS_LABELS[image.status] ?? image.status}
                    </Badge>
                    {image.doc_type && (
                      <Badge variant="outline">
                        {DOC_TYPE_LABELS[image.doc_type] ?? image.doc_type}
                      </Badge>
                    )}
                  </div>
                  {image.region && (
                    <div className="text-muted-foreground">
                      Regione:{' '}
                      <span className="text-foreground">{image.region}</span>
                    </div>
                  )}
                  {image.impression && (
                    <div className="text-muted-foreground">
                      Impressione:{' '}
                      <span className="font-medium text-foreground">
                        {image.impression}
                      </span>
                    </div>
                  )}
                  {image.findings.length > 0 && (
                    <ul className="list-disc list-inside text-muted-foreground">
                      {image.findings.map((finding, index) => (
                        <li key={index}>{finding}</li>
                      ))}
                    </ul>
                  )}
                  {image.limits.length > 0 && (
                    <div className="text-muted-foreground">
                      Limiti: {image.limits.join('; ')}
                    </div>
                  )}
                  {Object.keys(image.form_fields).length > 0 && (
                    <div className="rounded-md border border-border bg-background/60 p-2">
                      <div className="mb-0.5 font-medium text-muted-foreground">
                        Campi dal modulo
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3">
                        {Object.entries(image.form_fields).map(([name, value]) => (
                          <div key={name} className="flex gap-1">
                            <dt className="text-muted-foreground">{name}:</dt>
                            <dd className="text-foreground">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  {image.status !== 'ok' && (
                    <div className="flex items-start gap-1.5 text-danger">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                      <span>{image.reason ?? 'Motivo non disponibile'}</span>
                    </div>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => onOpen(image)}
                  >
                    <Maximize2 className="size-3" />
                    Apri immagine
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
        {documents.length > 0 && (
          <section data-testid="demo-documents-list" className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Documenti ({documents.length})
            </h3>
            {documents.map((document) => (
              <DemoDocumentCard
                key={`${document.sha256}-${document.logical_path}`}
                resultId={resultId}
                document={document}
              />
            ))}
          </section>
        )}
      </CardContent>
    </Card>
  );
}

/** Originale a schermo intero: reflow a container query (immagine + dettagli). */
export function DemoImageLightbox({
  resultId,
  image,
  onClose,
}: {
  resultId: string;
  image: DemoImageFinding | null;
  onClose: () => void;
}) {
  if (!image) return null;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid="demo-image-lightbox"
        className="flex h-fit max-h-[92dvh] w-fit! max-w-[95vw]! sm:max-w-[95vw]! flex-col overflow-hidden p-0"
      >
        <div className="flex max-h-[92dvh] min-h-0 flex-col lg:flex-row">
          <div className="flex min-h-0 min-w-0 items-center justify-center bg-muted/20 p-2">
            <DemoAttachmentImage
              resultId={resultId}
              sha256={image.sha256}
              page={image.page}
              alt={image.filename ?? 'Allegato'}
              className="h-auto w-auto max-h-[50dvh] max-w-[90vw] object-contain lg:max-h-[85dvh] lg:max-w-[calc(95vw-25rem)]"
            />
          </div>

          <aside className="max-h-[40dvh] shrink-0 space-y-3 overflow-y-auto border-t border-border bg-card p-4 text-xs lg:max-h-none lg:w-88 lg:border-t-0 lg:border-l">
            <div>
              <DialogTitle className="flex items-start gap-2 pr-8 text-base font-semibold text-foreground">
                <ImageIcon className="mt-0.5 size-4 shrink-0 text-primary" />
                {image.filename ?? image.logical_path}
              </DialogTitle>
              <DialogDescription className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <Badge variant={image.status === 'ok' ? 'secondary' : 'outline'}>
                  {STATUS_LABELS[image.status] ?? image.status}
                </Badge>
                {image.doc_type && (
                  <Badge variant="outline">
                    {DOC_TYPE_LABELS[image.doc_type] ?? image.doc_type}
                  </Badge>
                )}
              </DialogDescription>
            </div>
            {image.region && (
              <div className="text-muted-foreground">
                Regione:{' '}
                <span className="text-foreground">{image.region}</span>
              </div>
            )}
            {image.image_quality && (
              <div className="text-muted-foreground">
                Qualità:{' '}
                <span className="text-foreground">{image.image_quality}</span>
              </div>
            )}
            {image.impression && (
              <div className="text-muted-foreground">
                Impressione:{' '}
                <span className="font-medium text-foreground">
                  {image.impression}
                </span>
              </div>
            )}
            {image.findings.length > 0 && (
              <ul className="list-inside list-disc space-y-0.5 text-muted-foreground">
                {image.findings.map((finding, index) => (
                  <li key={index}>{finding}</li>
                ))}
              </ul>
            )}
            {image.limits.length > 0 && (
              <div className="text-muted-foreground">
                Limiti dichiarati: {image.limits.join('; ')}
              </div>
            )}
            {image.status !== 'ok' && (
              <div className="flex items-start gap-1.5 text-danger">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                <span>{image.reason ?? 'Motivo non disponibile'}</span>
              </div>
            )}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
